import React, { lazy } from 'react';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { GoogleReviews } from '../components/GoogleReviews';
import { CallbackForm } from '../components/CallbackForm';
import { MapPin, MessageCircle, Mail, Calendar, Instagram, Phone } from 'lucide-react';
import { useTranslation } from '../i18n';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
const TELEGRAM_URL = 'https://t.me/dentico_clinic';
const INSTAGRAM_URL = 'https://instagram.com/dentico_clinic';
const MAPS_EMBED_URL = 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1241.0181846982155!2d-0.104187760804084!3d51.53089276153758!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48760f7c6534e077%3A0xa54a590bd3b2a149!2sDentico%20Clinic%20-%20Private%20Dentist%20in%20Angel%2C%20Islington!5e0!3m2!1suk!2suk!4v1769722166469!5m2!1suk!2suk';
function TelegramIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" fill="currentColor" className={className} data-id="element-1634" data-mp="qyc62qiy7qtq">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" data-id="element-1635" data-mp="mzuy64rpecb9" />
    </svg>;
}
export function ContactPage() {
  const {
    t,
    lang
  } = useTranslation();
  const seoTitle = lang === 'en' ? 'Contact Dentico Clinic | Private Dentist Angel London' : lang === 'ua' ? 'Контакти Dentico Clinic | Приватний стоматолог Angel' : lang === 'ro' ? 'Contact Dentico Clinic | Dentist Privat Angel Londra' : 'Контакты Dentico Clinic | Частный стоматолог Angel';
  const seoDescription = lang === 'en' ? 'Contact Dentico Clinic in Angel, London. Book online, call, WhatsApp or visit our private dental clinic on City Road, EC1V.' : lang === 'ua' ? "Зв'яжіться з Dentico Clinic в Angel, Лондон. Записуйтесь онлайн, телефонуйте, пишіть у WhatsApp або відвідайте нашу клініку на City Road, EC1V." : lang === 'ro' ? 'Contactați Dentico Clinic din Angel, Londra. Programați online, sunați, WhatsApp sau vizitați clinica noastră privată de pe City Road, EC1V.' : 'Свяжитесь с Dentico Clinic в Angel, Лондон. Записывайтесь онлайн, звоните, пишите в WhatsApp или посетите нашу клинику на City Road, EC1V.';
  return <Layout title={seoTitle} description={seoDescription} data-id="element-1636" data-mp="h5q0fc91ijtx">
      <div className="bg-forest-900 py-20 text-white" data-id="element-1637" data-mp="21v3igf3uqqyk">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1638" data-mp="21tirt0knrs5z">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-1639" data-mp="1wuz5gz25ked3">
            {t('contact.title')}
          </h1>
          <p className="text-xl text-ivory/80 max-w-2xl mx-auto" data-id="element-1640" data-mp="2bjlk0yjyh239">
            {t('contact.subtitle')}
          </p>
          <p className="text-base text-ivory/60 max-w-2xl mx-auto mt-4" data-id="element-1641" data-mp="26mllr9tcl3mp">
            {lang === 'en' ? 'You can find Dentico Clinic at 360–364 City Road, London EC1V 2PY, close to Angel Station.' : lang === 'ua' ? 'Ви можете знайти Dentico Clinic за адресою 360–364 City Road, London EC1V 2PY, поруч зі станцією Angel.' : lang === 'ro' ? 'Puteți găsi Dentico Clinic la adresa 360–364 City Road, London EC1V 2PY, aproape de stația Angel.' : 'Вы можете найти Dentico Clinic по адресу 360–364 City Road, London EC1V 2PY, рядом со станцией Angel.'}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-id="element-1642" data-mp="18uoy68gnq6q9">
        <div className="grid lg:grid-cols-3 gap-12" data-id="element-1643" data-mp="1drymfksb8cci">
          {/* Contact Info */}
          <div className="lg:col-span-1 space-y-8" data-id="element-1644" data-mp="1hubpu2jl3y64">
            <Card className="p-8" data-id="element-1645" data-mp="1hsijkocvvfyn">
              <h2 className="text-2xl font-bold text-charcoal mb-6" data-id="element-1646" data-mp="1mt54hyalxv7v">
                {t('contact.getInTouch')}
              </h2>

              <div className="space-y-6" data-id="element-1647" data-mp="1rt18hxjb1mnf">
                {/* Address */}
                <div className="flex items-start gap-4" data-id="element-1648" data-mp="1wr2sckok1pzy">
                  <div className="p-2 bg-ivory-200 rounded-lg text-forest-600" data-id="element-1649" data-mp="1wprlgbztc24m">
                    <MapPin className="w-6 h-6" data-id="element-1650" data-mp="21jcdlneq61vw" />
                  </div>
                  <div data-id="element-1651" data-mp="25k5zyccir8mo">
                    <h3 className="font-semibold text-charcoal mb-1" data-id="element-1652" data-mp="25ictoy5tiqf7">
                      {t('contact.address')}
                    </h3>
                    <p className="text-charcoal-light" data-id="element-1653" data-mp="2agwg0zobq483">
                      360–364 City Rd
                      <br data-id="element-1654" data-mp="2ffpsqpvsdr77" />
                      London EC1V 2PY
                    </p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4" data-id="element-1655" data-mp="2klwezd36wfri">
                  <div className="p-2 bg-ivory-200 rounded-lg text-forest-600" data-id="element-1656" data-mp="2fnbcf70gtjzw">
                    <Phone className="w-6 h-6" data-id="element-1657" data-mp="2fpebshnicpha" />
                  </div>
                  <div data-id="element-1658" data-mp="2bnkg6ns66bnz">
                    <h3 className="font-semibold text-charcoal mb-1" data-id="element-1659" data-mp="2bocalva163co">
                      {lang === 'en' ? 'Phone' : lang === 'ua' ? 'Телефон' : lang === 'ro' ? 'Telefon' : 'Телефон'}
                    </h3>
                    <a href="tel:+447345197820" className="text-forest-600 hover:underline font-medium" data-id="element-1660" data-mp="2ukc79jdydv2l">
                      07345 197820
                    </a>
                  </div>
                </div>

                {/* WhatsApp */}
                <div className="flex items-start gap-4" data-id="element-1661" data-mp="1kezls718lugv">
                  <div className="p-2 bg-ivory-200 rounded-lg text-forest-600" data-id="element-1662" data-mp="1pe3znaz3wgdj">
                    <MessageCircle className="w-6 h-6" data-id="element-1663" data-mp="1aoxznksg2m4l" />
                  </div>
                  <div data-id="element-1664" data-mp="1amtxp8e3vx6y">
                    <h3 className="font-semibold text-charcoal mb-1" data-id="element-1665" data-mp="1fnhobsyfac0n">
                      {t('contact.whatsapp')}
                    </h3>
                    <p className="text-charcoal-light mb-2" data-id="element-1666" data-mp="11n6rm9afm37k">
                      {t('contact.whatsappDesc')}
                    </p>
                    <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-id="element-1667" data-mp="16kqa5le5x25b">
                      <Button size="sm" className="gap-2 bg-forest-600 text-white border border-forest-600 hover:bg-ivory hover:text-forest-600" data-id="element-1668" data-mp="wp5cu0k1gq1w">
                        <MessageCircle className="w-4 h-4" data-id="element-1669" data-mp="2r0trs2q9ssma" />
                        {t('contact.chatOnWhatsapp')}
                      </Button>
                    </a>
                  </div>
                </div>

                {/* Telegram */}
                <div className="flex items-start gap-4" data-id="element-1670" data-mp="p5nn25g7b3ww">
                  <div className="p-2 bg-ivory-200 rounded-lg text-forest-600" data-id="element-1671" data-mp="p778eagps55j">
                    <TelegramIcon className="w-6 h-6" data-id="element-1672" data-mp="kdcq9bgrby8k" />
                  </div>
                  <div data-id="element-1673" data-mp="y4r7bcr3tpcz">
                    <h3 className="font-semibold text-charcoal mb-1" data-id="element-1674" data-mp="y85d6uyz8gp6">
                      Telegram
                    </h3>
                    <p className="text-charcoal-light mb-2" data-id="element-1675" data-mp="183zzq16bmvx8">
                      {lang === 'en' ? 'Message us on Telegram' : lang === 'ua' ? 'Напишіть нам у Telegram' : lang === 'ro' ? 'Scrieți-ne pe Telegram' : 'Напишите нам в Telegram'}
                    </p>
                    <a href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer" data-id="element-1676" data-mp="2j44k5o09tqil">
                      <Button variant="outline" size="sm" className="gap-2" data-id="element-1677" data-mp="2st5vv1zs9lqr">
                        <TelegramIcon className="w-4 h-4" data-id="element-1678" data-mp="2sv8vmdqqmt8o" />
                        Telegram
                      </Button>
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-4" data-id="element-1679" data-mp="253lakcg5qceb">
                  <div className="p-2 bg-ivory-200 rounded-lg text-forest-600" data-id="element-1680" data-mp="2543g5zopm2yw">
                    <Mail className="w-6 h-6" data-id="element-1681" data-mp="2a1mzhe09l5xp" />
                  </div>
                  <div data-id="element-1682" data-mp="2f2aknj1task0">
                    <h3 className="font-semibold text-charcoal mb-1" data-id="element-1683" data-mp="2f2sniyizc50w">
                      {t('contact.email')}
                    </h3>
                    <a href="mailto:info@denticoclinic.co.uk" className="text-forest-600 hover:underline break-all" data-id="element-1684" data-mp="3ljxpbrpgrwae">
                      info@denticoclinic.co.uk
                    </a>
                  </div>
                </div>

                {/* Instagram */}
                <div className="flex items-start gap-4" data-id="element-1685" data-mp="153htd1xrwh55">
                  <div className="p-2 bg-ivory-200 rounded-lg text-forest-600" data-id="element-1686" data-mp="151wy1a3mwu68">
                    <Instagram className="w-6 h-6" data-id="element-1687" data-mp="1045aesa0vu7u" />
                  </div>
                  <div data-id="element-1688" data-mp="v5tymc0639mz">
                    <h3 className="font-semibold text-charcoal mb-1" data-id="element-1689" data-mp="v754chd6argq">
                      {t('contact.instagram')}
                    </h3>
                    <p className="text-charcoal-light mb-2" data-id="element-1690" data-mp="m4vwjz9ahc71">
                      {t('contact.instagramDesc')}
                    </p>
                    <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="text-forest-600 font-medium hover:underline" data-id="element-1691" data-mp="h6ud3d7ybav1">
                      @dentico_clinic
                    </a>
                  </div>
                </div>
              </div>

              {/* Book Online Button */}
              <div className="mt-8 pt-6 border-t border-beige/60" data-id="element-1692" data-mp="sfshymoruaqn">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-1693" data-mp="16gd15jylm8ko">
                  <Button fullWidth size="lg" className="gap-2" data-id="element-1694" data-mp="12erifhzdv02k">
                    <Calendar className="w-5 h-5" data-id="element-1695" data-mp="12cdnd94pdnv0" />
                    {t('nav.bookOnline')}
                  </Button>
                </a>
              </div>
            </Card>

            <Card className="p-8" data-id="element-1696" data-mp="9rdsi3u3r4ry">
              <h2 className="text-2xl font-bold text-charcoal mb-6" data-id="element-1697" data-mp="9nzpctdm6rjg">
                {t('contact.openingHours')}
              </h2>
              <div className="space-y-3" data-id="element-1698" data-mp="iq8ypfx5cev9">
                <div className="flex justify-between items-center pb-3 border-b border-beige/60" data-id="element-1699" data-mp="ip7ofdnyfwel">
                  <span className="text-charcoal-light" data-id="element-1700" data-mp="3r4hm89ktafgk">
                    {t('footer.monFri')}
                  </span>
                  <span className="font-medium text-charcoal" data-id="element-1701" data-mp="3n2w9co8a216d">
                    10:00 - 18:00
                  </span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-beige/60" data-id="element-1702" data-mp="3hsk54rf0dg9z">
                  <span className="text-charcoal-light" data-id="element-1703" data-mp="333now72sswya">
                    {t('footer.saturday')}
                  </span>
                  <span className="font-medium text-charcoal" data-id="element-1704" data-mp="3827eqikiisvx">
                    10:00 - 18:00
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-1705" data-mp="4kp5c6p3ddte">
                  <span className="text-charcoal-light" data-id="element-1706" data-mp="4iw7ngwgi3xj">
                    {t('footer.sunday')}
                  </span>
                  <span className="font-medium text-red-500" data-id="element-1707" data-mp="3qs6vmi6791o9">
                    {t('footer.closed')}
                  </span>
                </div>
              </div>
            </Card>
          </div>

          {/* Map */}
          <div className="lg:col-span-2" data-id="element-1708" data-mp="2fda67763sagd">
            <div className="bg-stone-200 rounded-2xl overflow-hidden h-full min-h-[500px] shadow-lg" data-id="element-1709" data-mp="2febjliald919">
              <iframe src={MAPS_EMBED_URL} width="100%" height="100%" style={{
              border: 0,
              minHeight: '500px'
            }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Dentico Clinic Location" data-id="element-1710" data-mp="lti1hgoflqil"></iframe>
            </div>
          </div>
        </div>
      </div>

      {/* Callback Form Section */}
      <section className="py-16 bg-ivory-200" data-id="element-1711" data-mp="3uch3l0s6nlqm">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1712" data-mp="3pc2tzeaxo3qh">
          <CallbackForm data-id="element-1713" data-mp="3kc6otbqi269c" />
        </div>
      </section>

      <GoogleReviews data-id="element-1714" data-mp="3kdhwvnqza069" />
    </Layout>;
}